<script setup lang="ts">
const { sourcesUrl, ijewelKey } = useRuntimeConfig().public;
const viewerInstance = ref<any>(null);
let activeCamera: any;
let cameraUpdateCallback: (() => void) | null = null;
const loading = ref(true);

const props = defineProps({
  imageId: {
    type: String,
    required: true,
  },
  materials: {
    type: Object as PropType<Property | null>,
    required: false,
  },
  scene: {
    type: Object as PropType<Property | null>,
    required: false,
  },
});

onMounted(async () => {
  await setupViewer();
  loading.value = false;
});

defineExpose({ goFullScreen });
function goFullScreen() {
  if (!viewerInstance.value) return;
  viewerInstance.value.getPluginByType("FullScreenPlugin").toggle();
}

async function setupViewer() {
  const pixotronics = await import(
    "https://dist.pixotronics.com/webgi/runtime/bundle-0.9.20.mjs"
  );
  const { ViewerApp, DiamondPlugin, GroundPlugin, addBasePlugins } =
    pixotronics;
  const viewer = new ViewerApp({
    canvas: document.getElementById("mcanvas"),
  });

  viewer.license = ijewelKey;
  viewer.renderer.displayCanvasScaling = 1.5;
  //console.log(viewer.userData);
  // Change this value to scale the configurator
  const rootScaleFactor = 1;
  viewer.scene.modelRoot.scale.set(
    rootScaleFactor,
    rootScaleFactor,
    rootScaleFactor
  );

  // Add all the plugins at once
  await addBasePlugins(viewer, {
    importPopup: false,
    enableDrop: false,
    interactionPrompt: false,
  });
  let diamondPlugin = await viewer.getOrAddPlugin(DiamondPlugin);
  diamondPlugin.setKey(ijewelKey);
  viewer.renderer.refreshPipeline();
  const ground = viewer.getPlugin(GroundPlugin);
  ground.autoBakeShadows = false;
  const manager = viewer.getManager();
  viewer.enabled = false;

  // Default layer definitions. Define all your layers here. Edit this structure to apply your own layer naming and materials

  const defaultLayerConfig: Record<string, string> = {};
  if (props.materials) {
    Object.assign(defaultLayerConfig, props.materials.value);
  }
  /* else {
    //Default materials test
    Object.assign(defaultLayerConfig, {
      metal: "testmat-silver.pmat",
      gem_02_MAT: "ruby.dmat",
      gem_03_MAT: "emerald.dmat",
      gem_01_MAT: "emerald.dmat",
    });
  }*/

  const baseURL = sourcesUrl + "/3dviewertest/materials/";

  interface LayerMaterialMap {
    [key: string]: any;
  }

  const layerMaterialMap: LayerMaterialMap = {};

  async function getOrCreateMaterial(layerName: string, matName: string) {
    let material = layerMaterialMap[matName];
    if (material !== undefined) {
      return material;
    } else {
      const matURL = baseURL + matName;
      if (matURL !== undefined) {
        material = await viewer.load(matURL);
        material.name = matName;
        layerMaterialMap[matName] = material;
        return material;
      } else {
        console.warn("Unable to find material for layer ", layerName);
      }
    }
  }

  async function applyMaterialToLayer(layerName: string, material: string) {
    const manager = viewer.getManager();
    const materials = manager.materials.findMaterialsByName(layerName);
    if (materials && materials.length > 0) {
      manager.materials.applyMaterial(material, layerName);
    } else {
      console.warn("Unable to find matching materials in Layers");
    }
  }

  async function applyLayerConfig(layerConfig: any) {
    for (let layerName in layerConfig) {
      const matName = layerConfig[layerName];
      const material = await getOrCreateMaterial(layerName, matName);
      // console.log(layerName, matName, material)
      applyMaterialToLayer(layerName, material);
    }
  }

  // Loads a 3d model, with auto scaling, and centering.
  async function loadModel(url: string) {
    const model = await viewer.load(url, {
      autoScale: true,
      autoCenter: true,
      license: ijewelKey,
    });
    return model;
  }

  // Load 3d model
  await loadModel(`${sourcesUrl}/marketplace/${props.imageId}.glb`);

  // Apply default layers
  await applyLayerConfig(defaultLayerConfig);

  // Load scene settings

  var sceneUrl;
  if (props.scene?.value) {
    sceneUrl = `${sourcesUrl}/marketplace/${props.scene.value}.vjson`;
  } else {
    sceneUrl = `${sourcesUrl}/marketplace/viewer-assets/default_scene.vjson`;
  }
  await viewer.load(sceneUrl);

  activeCamera = viewer.scene.activeCamera;
  cameraUpdateCallback = () => {
    const controls = activeCamera.getControls();
    controls.distance = 10;
    controls.minDistance = 5;
    controls.maxDistance = 15;
    controls.enablePan = false;
    controls.autoRotate = true;
  };
  activeCamera.addEventListener("update", cameraUpdateCallback);
  viewer.enabled = true;
  viewer.fitToView();
  viewerInstance.value = viewer;
  // manually bake the shadows once.
  ground.bakeShadows(); // required only if autoBakeShadows is false
}

onBeforeUnmount(() => {
  if (activeCamera && cameraUpdateCallback)
    activeCamera.removeEventListener("update", cameraUpdateCallback);
  viewerInstance.value.scene.disposeSceneModels();
  viewerInstance.value.getManager()?.materials?.dispose();
  disposeSceneObjects(viewerInstance.value.scene);
  viewerInstance.value.renderer.dispose();
  viewerInstance.value.scene.clear();
  viewerInstance.value.dispose();
  viewerInstance.value = null;
});

function disposeSceneObjects(scene: any) {
  scene.traverse((object: any) => {
    if (!object.isMesh) return;
    if (object.geometry) {
      object.geometry.dispose();
    }
    if (object.material) {
      if (Array.isArray(object.material)) {
        object.material.forEach((mat: any) => mat.dispose?.());
      } else {
        object.material.dispose?.();
      }
    }
  });
}
</script>

<template>
  <div class="relative w-full h-full">
    <div
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center bg-black/50">
      <LoaderSpinner size="2xl" />
    </div>
    <canvas id="mcanvas" class="w-full h-full"></canvas>
  </div>
</template>
